'use client';

import {
  useState,
  useEffect,
  useCallback,
  type FC,
  useContext,
  ReactNode,
} from 'react';
import type { ScheduledQuestionnaireDialogProps } from './ScheduledQuestionnaireDialog.types';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { getScheduledQuestionnaires } from '@/_api/admin-questionnaires';
import { ScheduledEmptyState } from './components/ScheduledEmptyState';
import { Alert, Button, Flex, Toggle, Dialog } from '@/_components';
import Link from 'next/link';
import { ScheduledQuestionnaire } from './components/ScheduledQuestionnaire';
import type { QuestionnaireTemplate } from '@/_types/questionnaireTemplates';
import { ScheduledShimmer } from './components/ScheduledShimmer';

export const ScheduledQuestionnaireDialog: FC<
  ScheduledQuestionnaireDialogProps
> = ({ isOpen, onClose, onSchedule }) => {
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  const [showMine, setShowMine] = useState<boolean>(false);
  const [showActive, setShowActive] = useState<boolean>(false);

  const onToggleMine = () => setShowMine(!showMine);
  const onToggleActive = () => setShowActive(!showActive);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [questionnaireTemplates, setQuestionnaireTemplates] = useState<
    Array<QuestionnaireTemplate>
  >([]);

  const loadScheduledQuestionnaires = useCallback(
    async (showMine: boolean, showActive: boolean) => {
      if (!token) return;

      try {
        setLoading(true);

        // Load questionnaires
        const questionnaireTemplates = await getScheduledQuestionnaires({
          show_active: showActive,
          show_mine: showMine,
          token,
        });

        setQuestionnaireTemplates(
          questionnaireTemplates as Array<QuestionnaireTemplate>,
        );
      } catch (error) {
        addToast('error', null, { error });
      } finally {
        setLoading(false);
      }
    },
    [token, addToast],
  );

  const loadDefaultScheduledQuestionnaires = useCallback(
    () => loadScheduledQuestionnaires(showMine, showActive),
    [showActive, showMine, loadScheduledQuestionnaires],
  );

  useEffect(() => {
    if (isOpen) {
      loadDefaultScheduledQuestionnaires();
    } else {
      setQuestionnaireTemplates([]);
      setLoading(false);
    }
  }, [isOpen, loadDefaultScheduledQuestionnaires, showMine, showActive]);

  const hasQuestionnaires: boolean =
    !isLoading && !!questionnaireTemplates?.length;

  const renderList: ReactNode = (
    <Flex isColumn={true} gap={12}>
      <Flex isColumn={true} gap={4}>
        {hasQuestionnaires ? (
          questionnaireTemplates?.map((questionnaireTemplate, key) => {
            return (
              <ScheduledQuestionnaire
                key={key}
                loadScheduledQuestionnaires={loadDefaultScheduledQuestionnaires}
                questionnaireTemplate={questionnaireTemplate}
              />
            );
          })
        ) : (
          <ScheduledEmptyState onSchedule={onSchedule} />
        )}
      </Flex>
      <Alert type="info" icon="info">
        Scheduled templates will be sent at 3PM using the timezone specified in{' '}
        <Link href="/firm">
          <b>Firm Settings</b>
        </Link>
        . You may cancel any scheduled templates before 2:50PM.
      </Alert>
      <Flex isColumn={true} gap={4}>
        <Toggle
          label="Show active only"
          isChecked={showActive}
          isSmall={true}
          onChange={onToggleActive}
        />
        <Toggle
          label="Show mine only"
          isChecked={showMine}
          isSmall={true}
          onChange={onToggleMine}
        />
      </Flex>
    </Flex>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Scheduled Questionnaires"
      headerButtons={
        hasQuestionnaires && (
          <Button
            value="New Questionnaire"
            onClick={onSchedule}
            size="small"
            intent="secondary"
            iconLeft="plus"
          />
        )
      }
    >
      <Dialog.Content>
        {isLoading ? <ScheduledShimmer /> : renderList}
      </Dialog.Content>
    </Dialog>
  );
};
